import React from 'react';
import { Trans } from 'react-i18next';
import { View } from 'react-native';

import { OrderState } from '@lib/types';
import { Typography } from '@components/Typography';
import { Icon, IconName } from '@components/Icon';
import { Theme, makeStyles, useTheme } from '@providers/Theme';

export type OrderStatusProps = {
  status: OrderState;
};

const statusColorMap: Record<OrderState, (theme: Theme) => string | undefined> =
  {
    PENDING: (theme) => theme.palette.yellow[500],
    ROUTED: (theme) => theme.palette.yellow[500],
    SEEN: (theme) => theme.palette.yellow[500],
    INSPECTING: (theme) => theme.palette.yellow[500],
    ACCEPTED: (theme) => theme.palette.success[400],
    FOOD_IS_READY: (theme) => theme.palette.success[400],
    OUT_FOR_DELIVERY: (theme) => theme.palette.success[400],
    DELIVERED: (theme) => theme.palette.success[400],
    CANCELED: (theme) => theme.palette.error[300],
    REJECTED: (theme) => theme.palette.error[300],
  };

const statusToTrans: Record<OrderState, string> = {
  PENDING: 'orderStatus.pending',
  ROUTED: 'orderStatus.pending',
  SEEN: 'orderStatus.pending',
  INSPECTING: 'orderStatus.inspected',
  ACCEPTED: 'orderStatus.accepted',
  FOOD_IS_READY: 'orderStatus.accepted',
  OUT_FOR_DELIVERY: 'orderStatus.outForDelivery',
  DELIVERED: 'orderStatus.delivered',
  CANCELED: 'orderStatus.cancelled',
  REJECTED: 'orderStatus.rejected',
};

const statusIconMap: Record<OrderState, IconName> = {
  PENDING: 'dots-horizontal',
  ROUTED: 'dots-horizontal',
  SEEN: 'dots-horizontal',
  INSPECTING: 'dots-horizontal',
  ACCEPTED: 'check-verified',
  FOOD_IS_READY: 'check-verified',
  OUT_FOR_DELIVERY: 'trips',
  DELIVERED: 'check-broken-circle',
  CANCELED: 'slash-circle',
  REJECTED: 'x-circle',
};

const useStyles = makeStyles<OrderStatusProps>((theme) => ({
  root: (props) => {
    const colorFn = (props?.status && statusColorMap[props.status]) 
      ? statusColorMap[props.status] 
      : statusColorMap.PENDING;

    return {
      flexDirection: 'row',
      alignItems: 'center',
      color: colorFn ? colorFn(theme) : theme.palette.yellow[500],
    };
  },
  icon: {
    marginRight: theme.spacing(0.5),
  },
}));

export const OrderStatus: React.FC<OrderStatusProps> = ({ status }) => {
  const isValidStatus = status in statusColorMap;
  const safeStatus = isValidStatus ? status : 'PENDING';

  const styles = useStyles({ status: safeStatus });
  const theme = useTheme();

  const getIconColor = () => {
    const colorFn = statusColorMap[safeStatus] || statusColorMap.PENDING;
    return colorFn(theme) ?? theme.palette.grey[500];
  };

  return (
    <View style={styles.root}>
      <Icon
        name={statusIconMap[safeStatus] || 'dots-horizontal'}
        size={16}
        color={getIconColor()}
        strokeWidth="2"
        style={styles.icon}
      />
      <Typography weight="semiBold" size={{ xs: 'md', lg: 'xl' }}>
        <Trans 
          i18nKey={statusToTrans[safeStatus] || 'orderStatus.pending'} 
        />
      </Typography>
    </View>
  );
};
